import { parseActivityMessage } from './message';
import type { ActivityJob } from './models';
import {
    isRecord,
    parseItemCounts,
    parseNonEmptyString,
    parseNullableNonEmptyString,
    parsePercent,
    parseStage,
    parseStatus,
    parseTimestamp,
} from './storage-validation';

export function parseStoredActivityJobWithIntegrity(
    value: unknown
): ParsedStoredActivityJob | null {
    if (!isRecord(value)) return null;

    const jobId = parseNonEmptyString(value['jobId']);
    const jobType = parseNonEmptyString(value['jobType']);
    const stage = parseStage(value['stage']);
    const status = parseStatus(value['status']);
    const warning = value['warning'];
    const percentComplete = parsePercent(value['percentComplete']);
    const itemCounts = parseItemCounts(value);
    const message = parseActivityMessage(value['message']);
    const occurredAtUtc = parseTimestamp(value['occurredAtUtc']);
    const documentPublicId = parseNullableNonEmptyString(
        value['documentPublicId']
    );
    const buildingPublicId = parseNullableNonEmptyString(
        value['buildingPublicId']
    );
    const storedPackId =
        value['packId'] === undefined
            ? null
            : parseNullableNonEmptyString(value['packId']);
    const storedExportKind = parseExportKind(value['exportKind']);
    const buildingName =
        value['buildingName'] === undefined
            ? null
            : parseNullableNonEmptyString(value['buildingName']);

    if (
        !jobId ||
        !jobType ||
        !stage ||
        !status ||
        (warning !== undefined && typeof warning !== 'boolean') ||
        percentComplete === undefined ||
        itemCounts === undefined ||
        message === undefined ||
        !occurredAtUtc ||
        documentPublicId === undefined ||
        buildingPublicId === undefined ||
        storedPackId === undefined ||
        storedExportKind === undefined ||
        buildingName === undefined ||
        (storedPackId !== null && buildingPublicId === null)
    ) {
        return null;
    }

    const exportIdentity = exportIdentityForStoredJob(jobType, storedPackId);
    if (exportIdentity.kind !== null && buildingPublicId === null) return null;
    const exportIdentityConflicted =
        (storedExportKind !== null &&
            storedExportKind !== exportIdentity.kind) ||
        storedPackId !== exportIdentity.packId;

    return {
        job: {
            jobId,
            jobType,
            stage,
            status,
            ...storedExportMetadata(jobType, warning, value),
            percentComplete,
            ...itemCounts,
            message:
                jobType === 'SelectedBuildingDocumentExport'
                    ? status === 'Completed'
                        ? 'Review the ZIP manifest for any omitted files.'
                        : null
                    : message,
            occurredAtUtc,
            documentPublicId,
            buildingPublicId,
            packId: exportIdentity.packId,
            exportKind: exportIdentity.kind,
            buildingName,
            selectedSelectionKey:
                typeof value['selectedSelectionKey'] === 'string'
                    ? value['selectedSelectionKey']
                    : undefined,
        },
        exportIdentityConflicted,
    };
}

export interface ParsedStoredActivityJob {
    readonly job: ActivityJob;
    readonly exportIdentityConflicted: boolean;
}

function exportIdentityForStoredJob(
    jobType: string,
    storedPackId: string | null
): {
    readonly kind: 'documents' | 'selected-documents' | 'pack' | null;
    readonly packId: string | null;
} {
    if (jobType === 'SelectedBuildingDocumentExport')
        return { kind: 'selected-documents', packId: null };
    if (jobType === 'BuildingDocumentExport') {
        return { kind: 'documents', packId: null };
    }
    if (jobType === 'DocumentPackExport') {
        return { kind: 'pack', packId: storedPackId };
    }
    return { kind: null, packId: null };
}

function parseExportKind(
    value: unknown
): ActivityJob['exportKind'] | undefined {
    if (value === undefined || value === null) return null;
    if (
        value === 'documents' ||
        value === 'selected-documents' ||
        value === 'pack'
    ) {
        return value;
    }
    return undefined;
}

function storedExportMetadata(
    jobType: string,
    warning: boolean | undefined,
    value: Record<string, unknown>
): Pick<ActivityJob, 'warning' | 'downloadAvailable' | 'expiresAt'> {
    if (jobType !== 'SelectedBuildingDocumentExport') {
        return {
            warning: warning ?? false,
            downloadAvailable: undefined,
            expiresAt: undefined,
        };
    }
    return {
        warning: false,
        downloadAvailable: false,
        expiresAt:
            typeof value['expiresAt'] === 'string' ? value['expiresAt'] : null,
    };
}
